import React from 'react';
import { Link } from 'react-router-dom';
import { Plane, Shield, Upload, Calendar, CheckCircle2, XCircle, AlertTriangle, FileText, Lock, Minus } from 'lucide-react';

const DataPrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A2B4A] via-[#243552] to-[#1A2B4A]">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#1A2B4A]/95 backdrop-blur-sm border-b border-[#4A90E2]/20 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Plane className="h-8 w-8 text-[#4A90E2]" />
              <span className="text-2xl font-heading font-bold text-white">Winglet</span>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/how-it-works" className="text-gray-300 hover:text-[#4A90E2] transition-colors">How It Works</Link>
              <Link to="/pricing" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Pricing</Link>
              <Link to="/compare-tools" className="text-gray-300 hover:text-[#4A90E2] transition-colors">Compare</Link>
              <Link to="/about" className="text-gray-300 hover:text-[#4A90E2] transition-colors">About</Link>
              <Link to="/data-privacy" className="text-[#4A90E2] border-b-2 border-[#4A90E2]">Data Privacy</Link>
            </div>
            <div className="flex space-x-4">
              <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
              <Link to="/signup" className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-6 py-2 rounded-lg transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-block mb-6">
            <Shield className="h-16 w-16 text-[#4A90E2] mx-auto" />
          </div>
          <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
            Your Data. <span className="text-[#4A90E2]">Your Control.</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            We never access company systems. We only work with data you directly upload or sync from your calendar. Here's exactly what we do—and what we don't.
          </p>
        </div>
      </section>

      {/* Critical Disclaimer */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-amber-500/10 border-2 border-amber-500/30 rounded-xl p-8">
            <div className="flex items-start space-x-4">
              <AlertTriangle className="h-8 w-8 text-amber-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl font-heading font-bold text-white mb-3">We Are NOT Flight Crew Viewer</h3>
                <div className="space-y-3 text-gray-300">
                  <p className="text-lg">
                    <span className="font-semibold text-white">Winglet has ZERO access to:</span>
                  </p>
                  <ul className="space-y-2 ml-6">
                    <li>• Delta's Flight Crew Viewer system</li>
                    <li>• Flighty or any other airline systems</li>
                    <li>• Your company email or internal tools</li>
                    <li>• Any data we didn't receive directly from you</li>
                  </ul>
                  <p className="text-lg font-semibold text-white pt-4">
                    We are like WidgetCrew: 100% user-controlled. No more, no less.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Access */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-4">
            What We <span className="text-green-400">DO</span> Access
          </h2>
          <p className="text-xl text-gray-400 text-center mb-12">
            Only what you explicitly provide
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* User Uploads */}
            <div className="bg-[#1A2B4A] border-2 border-green-500/30 rounded-xl p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Upload className="h-6 w-6 text-green-400" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-white">User Uploads</h3>
              </div>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Rotation PDFs</p>
                    <p className="text-sm">Your green slips or ICS downloads that you manually upload</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Timecard PDFs</p>
                    <p className="text-sm">Your monthly timecards that you manually upload</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Manual Events</p>
                    <p className="text-sm">Sick days, training, vacation you manually enter</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Calendar Sync */}
            <div className="bg-[#1A2B4A] border-2 border-green-500/30 rounded-xl p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-green-400" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-white">Calendar Sync (Optional)</h3>
              </div>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">ICS Calendar Events</p>
                    <p className="text-sm">Flight schedule events you've already added to your personal calendar</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">You Control Sync</p>
                    <p className="text-sm">You choose when to sync. We never auto-pull without permission</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-semibold text-white">Only Flight Data</p>
                    <p className="text-sm">We only read events tagged as "flight" or "rotation"—never personal events</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We DON'T Access */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-4">
            What We <span className="text-red-400">DON'T</span> Access
          </h2>
          <p className="text-xl text-gray-400 text-center mb-12">
            Zero access to company systems
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            <DataDontAccess
              icon={XCircle}
              title="Flight Crew Viewer"
              description="No access to Delta's scheduling system, bid submissions, or crew portal"
            />
            <DataDontAccess
              icon={XCircle}
              title="Flighty / Other Tools"
              description="We don't connect to Flighty, ForeFlight, or any third-party aviation apps"
            />
            <DataDontAccess
              icon={XCircle}
              title="Company Email"
              description="No access to your Delta email, company messages, or internal communications"
            />
            <DataDontAccess
              icon={XCircle}
              title="Payroll Systems"
              description="We never connect directly to payroll databases or financial systems"
            />
            <DataDontAccess
              icon={XCircle}
              title="Personnel Records"
              description="No access to seniority lists, medical records, training files, or HR systems"
            />
            <DataDontAccess
              icon={XCircle}
              title="Real-Time Tracking"
              description="We don't track your physical location or monitor your activities"
            />
          </div>
        </div>
      </section>

      {/* How We're Different */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-heading font-bold text-white text-center mb-12">
            How Winglet Compares
          </h2>
          
          <div className="space-y-6">
            <ComparisonRow
              feature="Data Source"
              winglet="User uploads only (rotations, timecards)"
              otherTools="May access Flight Crew Viewer, company APIs"
              wingletGood={true}
            />
            <ComparisonRow
              feature="Company System Access"
              winglet="Zero access to Delta systems"
              otherTools="Some tools connect directly to airline databases"
              wingletGood={true}
            />
            <ComparisonRow
              feature="Calendar Integration"
              winglet="Optional ICS sync (you control when)"
              otherTools="May auto-sync without explicit permission"
              wingletGood={true}
            />
            <ComparisonRow
              feature="Data Ownership"
              winglet="You own your data. Export anytime. Delete on cancellation."
              otherTools="Varies by tool"
              wingletGood={true}
            />
            <ComparisonRow
              feature="Third-Party Sharing"
              winglet="Never. Your data stays with Winglet."
              otherTools="May share with partners, advertisers"
              wingletGood={true}
            />
          </div>
        </div>
      </section>

      {/* What We DON'T Do */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#1A2B4A] border-2 border-[#4A90E2]/30 rounded-xl p-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-12 h-12 bg-[#4A90E2]/20 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-[#4A90E2]" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-white">We Don't File Grievances</h2>
            </div>
            
            <div className="space-y-4 text-gray-300">
              <p className="text-lg">
                <span className="font-semibold text-white">Winglet is a tool, not a union representative.</span>
              </p>
              
              <p>
                Once we've identified pay discrepancies or FAR 117 violations, we provide you with:
              </p>
              
              <ul className="space-y-2 ml-6">
                <li className="flex items-start space-x-2">
                  <CheckCircle2 className="h-5 w-5 text-[#4A90E2] flex-shrink-0 mt-1" />
                  <span>Detailed PDF reports showing the discrepancy</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle2 className="h-5 w-5 text-[#4A90E2] flex-shrink-0 mt-1" />
                  <span>PWA section references supporting your claim</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle2 className="h-5 w-5 text-[#4A90E2] flex-shrink-0 mt-1" />
                  <span>Side-by-side comparison of company vs. correct calculations</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle2 className="h-5 w-5 text-[#4A90E2] flex-shrink-0 mt-1" />
                  <span>Export-ready documents for your union rep</span>
                </li>
              </ul>
              
              <div className="bg-[#0F1824] border border-[#4A90E2]/20 rounded-lg p-6 mt-6">
                <p className="text-white font-semibold mb-2">You Take It From There</p>
                <p>
                  You bring the PDF to your union representative. They file the grievance. We give you the proof—you get the money.
                </p>
              </div>
              
              <p className="text-sm text-gray-400 pt-4">
                <span className="text-amber-400 font-semibold">Why?</span> We don't work for the union. We don't work for the company. We work for you. Our job is accuracy and transparency—not legal representation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Support */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0F1824]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-heading font-bold text-white mb-6">
            Who We Support
          </h2>
          
          <div className="bg-[#1A2B4A] border border-[#4A90E2]/30 rounded-xl p-8">
            <div className="space-y-6 text-gray-300">
              <div>
                <p className="text-2xl font-semibold text-white mb-2">Currently:</p>
                <p className="text-xl text-[#4A90E2] font-semibold">Delta Air Lines Pilots</p>
              </div>
              
              <div className="h-px bg-[#4A90E2]/20 my-6"></div>
              
              <div>
                <p className="text-2xl font-semibold text-white mb-2">Coming Soon:</p>
                <ul className="space-y-2 text-lg">
                  <li className="text-gray-300">Delta Flight Attendants</li>
                  <li className="text-gray-400">United Airlines</li>
                  <li className="text-gray-400">American Airlines</li>
                  <li className="text-gray-400">Southwest Airlines</li>
                </ul>
              </div>
              
              <p className="text-sm text-gray-400 pt-4">
                Each airline has different pay rules, reroute logic, and scheduling systems. We're building Winglet one airline at a time to ensure 100% accuracy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-[#4A90E2]/20 to-[#4A90E2]/10 border border-[#4A90E2]/30 rounded-xl p-12">
            <Lock className="h-16 w-16 text-[#4A90E2] mx-auto mb-6" />
            <h2 className="text-4xl font-heading font-bold text-white mb-4">
              Your Data. Your Rules.
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              No company access. No hidden connections. Just accurate pay calculations from the data you provide.
            </p>
            <Link 
              to="/signup" 
              className="bg-[#4A90E2] hover:bg-[#3A7BC8] text-white px-8 py-4 rounded-lg text-lg font-semibold inline-block transition-all transform hover:scale-105"
            >
              Start Free Trial - 1.5 Months
            </Link>
            <p className="text-sm text-gray-400 mt-4">No credit card required</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F1824] border-t border-[#4A90E2]/20 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Plane className="h-6 w-6 text-[#4A90E2]" />
                <span className="text-xl font-heading font-bold text-white">Winglet</span>
              </div>
              <p className="text-gray-400 text-sm">Your AI co-pilot for accurate pay and FAR 117 protection.</p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#4A90E2]">How It Works</Link></li>
                <li><Link to="/pricing" className="text-gray-400 hover:text-[#4A90E2]">Pricing</Link></li>
                <li><Link to="/compare-tools" className="text-gray-400 hover:text-[#4A90E2]">Compare</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-400 hover:text-[#4A90E2]">About</Link></li>
                <li><Link to="/data-privacy" className="text-gray-400 hover:text-[#4A90E2]">Data Privacy</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#4A90E2]">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-[#4A90E2]">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-[#4A90E2]">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#4A90E2]/20 pt-8 text-center text-gray-400 text-sm">
            <p>© 2026 Winglet. All rights reserved. Built by pilots, for pilots.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Helper Components
const DataDontAccess = ({ icon: Icon, title, description }) => (
  <div className="bg-[#1A2B4A] border-2 border-red-500/30 rounded-xl p-6">
    <div className="flex items-center space-x-3 mb-4">
      <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
        <Icon className="h-5 w-5 text-red-400" />
      </div>
      <h3 className="text-lg font-heading font-bold text-white">{title}</h3>
    </div>
    <p className="text-gray-300 text-sm">{description}</p>
  </div>
);

const ComparisonRow = ({ feature, winglet, otherTools, wingletGood }) => (
  <div className="bg-[#1A2B4A] border border-[#4A90E2]/20 rounded-lg p-6">
    <h4 className="text-white font-semibold mb-4">{feature}</h4>
    <div className="grid md:grid-cols-2 gap-6">
      <div className="flex items-start space-x-3">
        {wingletGood ? (
          <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0 mt-1" />
        ) : (
          <XCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-1" />
        )}
        <div>
          <p className="text-xs text-[#4A90E2] font-semibold uppercase mb-1">Winglet</p>
          <p className="text-gray-300 text-sm">{winglet}</p>
        </div>
      </div>
      <div className="flex items-start space-x-3">
        {!wingletGood ? (
          <CheckCircle2 className="h-5 w-5 text-green-400 flex-shrink-0 mt-1" />
        ) : (
          <Minus className="h-5 w-5 text-gray-500 flex-shrink-0 mt-1" />
        )}
        <div>
          <p className="text-xs text-gray-500 font-semibold uppercase mb-1">Other Tools</p>
          <p className="text-gray-400 text-sm">{otherTools}</p>
        </div>
      </div>
    </div>
  </div>
);

const Footer = () => (
  <footer className="bg-[#0F1824] border-t border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
        {/* Branding */}
        <div>
          <div className="flex items-center space-x-2 mb-4">
            <Plane className="h-6 w-6 text-[#4A90E2]" />
            <span className="text-xl font-heading font-bold text-white">Winglet</span>
          </div>
          <p className="text-gray-400 text-sm">The AI-powered co-pilot for your career.</p>
        </div>
        
        {/* Product Section */}
        <div>
          <h4 className="text-white font-semibold mb-4">Product</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/how-it-works" className="text-gray-400 hover:text-[#4A90E2] transition-colors">How It Works</Link></li>
            <li><Link to="/pricing" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Pricing</Link></li>
            <li><Link to="/compare-tools" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Compare</Link></li>
          </ul>
        </div>
        
        {/* Company Section */}
        <div>
          <h4 className="text-white font-semibold mb-4">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="text-gray-400 hover:text-[#4A90E2] transition-colors">About Us</Link></li>
            <li><Link to="/contact" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Contact</Link></li>
            <li><Link to="/data-privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Data Privacy</Link></li>
          </ul>
        </div>
        
        {/* Legal Section */}
        <div>
          <h4 className="text-white font-semibold mb-4">Legal</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Privacy Policy</Link></li>
            <li><Link to="/terms" className="text-gray-400 hover:text-[#4A90E2] transition-colors">Terms of Service</Link></li>
          </ul>
        </div>
      </div>
      
      {/* Copyright */}
      <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
        <p>© 2026 Winglet. Built by pilots, for pilots. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

export default DataPrivacyPage;
